import { getSubunitMultiplier } from '@bbpos/utils';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  PageContainer,
  ErrorBoundary,
  Input,
  Select,
  Button,
  DataTable,
  Skeleton,
  StatCard,
  EmptyState,
  Modal,
  useToast,
} from '@bbpos/ui';
import type { ColumnDef } from '@bbpos/ui';
import type { CurrencyCode } from '@bbpos/types';
import { formatPaise } from '@/utils/currency';
import { ROLE_LEVELS } from '@/types/user';
import { useAuthStore } from '@/stores/use-auth-store';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { useTenantCurrency, getCurrencySymbol } from '@/hooks/use-tenant-currency';
import { useTenantDial } from '@/hooks/use-tenant-dial';
import {
  useCreditAccounts,
  useCreateCreditAccount,
  AccountStatusPill,
} from '@/features/credits';
import type { CreditAccount, AccountStatus, CreateAccountInput } from '@/features/credits';
import { customersApi } from '@/features/customers';
import type { Customer } from '@/features/customers';

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'OVERDUE', label: 'Overdue' },
  { value: 'FROZEN', label: 'Frozen' },
  { value: 'CLOSED', label: 'Closed' },
];

type Row = CreditAccount;

function utilColor(outstanding: number, limit: number): string {
  if (limit === 0) return 'text-neutral-700 dark:text-neutral-200';
  const pct = (outstanding / limit) * 100;
  if (pct > 90) return 'text-danger-600 dark:text-danger-400';
  if (pct > 60) return 'text-amber-600 dark:text-amber-400';
  return 'text-neutral-700 dark:text-neutral-200';
}

function formatDateIN(dateStr: string | null): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function makeColumns(currency: CurrencyCode): ColumnDef<Row>[] {
  return [
  {
    key: 'customer_name',
    header: 'Customer',
    render: (_v, row) => (
      <Link
        to={`/credit-accounts/${row.id}`}
        className="group flex items-center gap-2 -my-1 rounded-lg px-2 py-1 transition-colors hover:bg-emerald-50 dark:hover:bg-emerald-900/20"
      >
        <p className="text-sm font-bold text-neutral-900 dark:text-neutral-100 group-hover:text-emerald-700 dark:group-hover:text-emerald-300">
          {row.customer_name}
        </p>
      </Link>
    ),
  },
  {
    key: 'customer_phone',
    header: 'Phone',
    render: (_v, row) => (
      <span className="text-sm text-neutral-500 dark:text-neutral-400 font-mono tabular-nums">
        {row.customer_phone ?? '—'}
      </span>
    ),
  },
  {
    key: 'credit_limit',
    header: 'Credit Limit',
    numeric: true,
    render: (_v, row) => (
      <span className="text-sm font-bold text-neutral-900 dark:text-neutral-100 tabular-nums">
        {row.credit_limit === 0 ? 'Unlimited' : formatPaise(row.credit_limit, currency)}
      </span>
    ),
  },
  {
    key: 'outstanding_balance',
    header: 'Outstanding',
    numeric: true,
    render: (_v, row) => (
      <span className={`text-sm font-bold tabular-nums ${utilColor(row.outstanding_balance, row.credit_limit)}`}>
        {formatPaise(row.outstanding_balance, currency)}
      </span>
    ),
  },
  {
    key: 'created_at' as keyof Row,
    header: 'Available',
    numeric: true,
    render: (_v, row) => {
      const avail = row.credit_limit === 0 ? null : row.credit_limit - row.outstanding_balance;
      return (
        <span className="text-sm tabular-nums text-success-600 dark:text-success-400">
          {avail == null ? '—' : formatPaise(avail, currency)}
        </span>
      );
    },
  },
  {
    key: 'last_payment_date',
    header: 'Last Payment',
    render: (_v, row) => (
      <span className="text-xs text-neutral-500 dark:text-neutral-400 tabular-nums">
        {formatDateIN(row.last_payment_date)}
      </span>
    ),
  },
  {
    key: 'status',
    header: 'Status',
    render: (_v, row) => <AccountStatusPill status={row.status} />,
  },
  ];
}

function CreditAccountsListContent() {
  const dial = useTenantDial();
  const navigate = useNavigate();
  const { toast } = useToast();
  const currency = useTenantCurrency();
  const [searchParams, setSearchParams] = useSearchParams();
  const searchRef = useRef<HTMLInputElement | null>(null);
  const flags = useFeatureFlag();

  const role = useAuthStore((s) => s.profile?.role ?? '');
  const roleLevel = ROLE_LEVELS[role] ?? 0;
  const canCreate = roleLevel >= ROLE_LEVELS.OWNER;

  const page = Number(searchParams.get('page') ?? '1');
  const search = searchParams.get('search') ?? '';
  const status = (searchParams.get('status') ?? '') as AccountStatus | '';

  const { data, isLoading, isFetching } = useCreditAccounts({
    page,
    search: search || undefined,
    status: status || undefined,
  });
  const rows = useMemo<Row[]>(() => data?.data ?? [], [data]);
  const columns = useMemo(() => makeColumns(currency), [currency]);

  const totalOutstanding = useMemo(() => rows.reduce((s, r) => s + r.outstanding_balance, 0), [rows]);
  const totalLimit = useMemo(() => rows.reduce((s, r) => s + r.credit_limit, 0), [rows]);
  const overdueCount = useMemo(() => rows.filter((r) => r.status === 'OVERDUE').length, [rows]);

  const createMutation = useCreateCreditAccount();
  const [showCreate, setShowCreate] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [creditLimitRupees, setCreditLimitRupees] = useState('');

  // ── Customer name typeahead (3+ chars → suggestions, autofill phone on pick) ──
  const tenantId = useAuthStore((s) => s.profile?.tenant_id ?? '');
  const [showSuggest, setShowSuggest] = useState(false);
  const [debouncedName, setDebouncedName] = useState('');
  const comboRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedName(customerName.trim()), 250);
    return () => clearTimeout(t);
  }, [customerName]);

  const customerSearch = useQuery({
    queryKey: ['customers', tenantId, 'credit-typeahead', debouncedName],
    queryFn: () => customersApi.list({ page: 1, limit: 8, search: debouncedName }),
    enabled: showSuggest && debouncedName.length >= 3,
    staleTime: 30_000,
  });
  const suggestions =
    showSuggest && debouncedName.length >= 3 ? (customerSearch.data?.data ?? []) : [];

  const selectCustomer = useCallback((c: Customer) => {
    setCustomerName(c.name);
    setCustomerPhone(c.phone ?? '');
    setCustomerId(c.id);
    setDebouncedName(c.name);
    setShowSuggest(false);
  }, []);

  // Close the suggestion list on outside click.
  useEffect(() => {
    if (!showSuggest) return;
    function onDown(e: MouseEvent) {
      if (comboRef.current && !comboRef.current.contains(e.target as Node)) setShowSuggest(false);
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [showSuggest]);

  function setParam(key: string, value: string) {
    const params = new URLSearchParams(searchParams);
    if (value) params.set(key, value);
    else params.delete(key);
    if (key !== 'page') params.set('page', '1');
    setSearchParams(params);
  }

  // Feature flag gate
  useEffect(() => {
    if (!flags.ENABLE_CREDIT_BILLING) navigate('/dashboard', { replace: true });
  }, [flags, navigate]);

  // Keyboard: n → create, / → search
  const canCreateRef = useRef(canCreate);
  canCreateRef.current = canCreate;
  useEffect(() => {
    function handler(e: KeyboardEvent) {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === 'n' && canCreateRef.current) { e.preventDefault(); setShowCreate(true); }
      if (e.key === '/') { e.preventDefault(); searchRef.current?.focus(); }
    }
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const handleCreate = useCallback(() => {
    if (!customerName.trim()) { toast({ variant: 'warning', title: 'Customer name required' }); return; }
    if (customerPhone && !/^\+[1-9]\d{6,14}$/.test(customerPhone)) {
      toast({ variant: 'warning', title: 'Invalid phone', description: 'Enter a valid phone number' });
      return;
    }
    const phone = customerPhone;
    const limitPaise = creditLimitRupees
      ? Math.round(Number(creditLimitRupees) * getSubunitMultiplier(currency))
      : undefined;
    if (creditLimitRupees && (!Number.isFinite(limitPaise) || (limitPaise !== undefined && limitPaise < 0))) {
      toast({ variant: 'warning', title: 'Invalid credit limit' }); return;
    }
    const input: CreateAccountInput = {
      customer_id: customerId ?? undefined,
      customer_name: customerName.trim(),
      customer_phone: phone || undefined,
      credit_limit: limitPaise,
    };
    createMutation.mutate(input, {
      onSuccess: () => {
        toast({ variant: 'success', title: 'Account created' });
        setShowCreate(false);
        setShowSuggest(false);
        setCustomerName('');
        setCustomerPhone('');
        setCustomerId(null);
        setCreditLimitRupees('');
      },
      onError: (err) => { toast({ variant: 'error', title: 'Failed', description: (err as Error).message }); },
    });
  }, [customerId, customerName, customerPhone, creditLimitRupees, createMutation, toast]);

  if (!flags.ENABLE_CREDIT_BILLING) return null;

  return (
    <div className="animate-fade-in">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-500 p-6 sm:p-7 text-white mb-6 shadow-[0_25px_70px_-25px_rgba(16,185,129,0.7)]">
        <div className="pointer-events-none absolute -top-24 -right-16 h-56 w-56 rounded-full bg-white/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-16 -left-10 h-48 w-48 rounded-full bg-black/15 blur-3xl" />
        <div className="relative flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-[10px] font-bold uppercase tracking-wider ring-1 ring-white/25 backdrop-blur-sm">
              Credit Billing
            </span>
            <h1 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight text-white">Credit Accounts</h1>
            <p className="mt-1.5 text-sm text-white/90 max-w-xl">
              Manage deferred payment accounts — track limits, balances, and collections.
            </p>
          </div>
          {canCreate ? (
            <Button variant="primary" onClick={() => setShowCreate(true)} className="!bg-white !text-emerald-700 hover:!bg-white/90 !border-transparent">
              + New Account
            </Button>
          ) : null}
        </div>
      </div>

      {/* KPI Cards — using StatCard from @bbpos/ui */}
      {isLoading ? (
        <div className="grid grid-cols-3 gap-4 mb-6">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} variant="rect" height={100} className="rounded-xl" />)}</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <StatCard
            title="Total Outstanding"
            value={formatPaise(totalOutstanding, currency)}
            variant="default"
            icon={<svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
            anomaly={totalOutstanding > totalLimit * 0.9 && totalLimit > 0}
          />
          <StatCard
            title="Total Credit Limit"
            value={formatPaise(totalLimit, currency)}
            variant="default"
            icon={<svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" /></svg>}
          />
          <StatCard
            title="Overdue Accounts"
            value={String(overdueCount)}
            variant="default"
            icon={<svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>}
            anomaly={overdueCount > 0}
          />
        </div>
      )}

      {/* Filters — search + status */}
      <div className="rounded-xl backdrop-blur-sm bg-white/80 dark:bg-neutral-800/80 shadow-md p-4 sm:p-5 mb-6">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <Input
              ref={searchRef}
              placeholder="Search customers..."
              value={search}
              onChange={(v) => setParam('search', v)}
              aria-label="Search credit accounts"
            />
          </div>
          <div className="w-full sm:w-44">
            <Select
              options={STATUS_OPTIONS}
              value={status}
              onChange={(v) => setParam('status', typeof v === 'string' ? v : '')}
              aria-label="Filter by status"
            />
          </div>
        </div>
      </div>

      {/* Data */}
      {isLoading ? (
        <div className="space-y-2">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} variant="rect" height={56} className="rounded-lg" />)}</div>
      ) : rows.length === 0 && !isFetching ? (
        <div className="rounded-2xl backdrop-blur-sm bg-white/80 dark:bg-neutral-800/80 shadow-md p-10 animate-fade-in">
          <EmptyState variant="page" title="No credit accounts" description="Create a credit account to enable deferred payment for customers."
            action={canCreate ? { label: '+ New Account', onClick: () => setShowCreate(true) } : undefined}
            icon={<svg className="h-20 w-20 text-emerald-300 dark:text-emerald-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.3}><path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" /></svg>}
          />
        </div>
      ) : (
        <section className="rounded-2xl backdrop-blur-sm bg-white/80 dark:bg-neutral-800/80 shadow-md overflow-hidden animate-slide-up">
          <DataTable<Row> columns={columns} data={rows} loading={isFetching} totalCount={data?.meta.total} currentPage={page} pageSize={20} onPageChange={(p) => setParam('page', String(p))} stickyHeader />
        </section>
      )}

      {/* Create Modal */}
      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="New Credit Account" variant="wide">
        <div className="space-y-4">
          <div className="relative" ref={comboRef}>
            <Input
              label="Customer Name"
              value={customerName}
              onChange={(v) => { setCustomerName(v.slice(0, 200)); setCustomerId(null); setShowSuggest(true); }}
              onKeyDown={(e) => { if (e.key === 'Escape') setShowSuggest(false); }}
              placeholder="Type 3+ letters to search…"
              required
            />
            {showSuggest && debouncedName.length >= 3 && (
              <div className="absolute z-50 mt-1 w-full rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 shadow-lg max-h-60 overflow-y-auto">
                {customerSearch.isLoading ? (
                  <div className="px-3 py-2 text-sm text-neutral-500 dark:text-neutral-400">Searching…</div>
                ) : suggestions.length === 0 ? (
                  <div className="px-3 py-2 text-sm text-neutral-500 dark:text-neutral-400">No matching customers</div>
                ) : (
                  suggestions.map((c) => (
                    <button
                      type="button"
                      key={c.id}
                      onClick={() => selectCustomer(c)}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-brand-50 dark:hover:bg-neutral-700 flex items-center justify-between gap-2"
                    >
                      <span className="font-medium text-neutral-900 dark:text-neutral-100 truncate">{c.name}</span>
                      <span className="text-xs font-mono tabular-nums text-neutral-500 dark:text-neutral-400 shrink-0">{c.phone}</span>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
          <Input label="Phone" variant="phone" defaultDial={dial} value={customerPhone} onChange={setCustomerPhone} placeholder="98765 43210" />
          <Input label={`Credit Limit (${getCurrencySymbol(currency)})`} type="number" min={0} value={creditLimitRupees} onChange={setCreditLimitRupees} helperText="Enter amount in rupees. Leave 0 or empty for no limit." />
          <div className="flex items-center justify-end gap-3 pt-2">
            <Button variant="secondary" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button variant="primary" onClick={handleCreate} loading={createMutation.isPending}>Create Account</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export function CreditAccountsListPage() {
  return (
    <PageContainer title="Credit Accounts" breadcrumbs={[{ label: 'Home', href: '/home' }, { label: 'Credit Accounts' }]}>
      <ErrorBoundary resetKey="credit-accounts-list">
        <CreditAccountsListContent />
      </ErrorBoundary>
    </PageContainer>
  );
}
